import React, { FC } from 'react'
import { useHistory } from 'react-router-dom'
import moment from 'moment'
import { InfoIcon, RightArrowIcon } from 'transfix-icons/build'
import LaneArrowIcon from 'transfix-icons/build/LaneArrowIcon'
import Card from 'transfix-ui/components/Card'
import { FormattedDateTime } from 'transfix-ui/components/FormattedDateTime'
import { Tooltip } from 'transfix-ui/components/Tooltip/Tooltip'
import { Box, Grid, makeStyles, Typography } from 'transfix-ui/core'
import { brandColors } from 'transfix-ui/theme'
import { useFeatureFlagContext } from 'utils/context/FeatureFlagsContext'
import { useFMSAnalytics } from 'apps/fleet-planner/hooks/useFMSAnalytics'
import { DedicatedLane } from 'apps/fleet-planner/types/apiTypes'
import { shouldShowExpirationTag } from 'apps/fleet-planner/utils/dedicatedLanesHelpers'
import formatCurrency from 'utils/formatCurrency'
import { AllowedTimezones } from 'constants/AllowedTimezones'
import { DedicatedLanesExpiringBadge } from './DedicatedLanesExpiringBadge'

const useDedicatedLaneStyles = makeStyles(() => ({
  root: {
    '&:hover': {
      backgroundColor: brandColors.coolGray1,
      cursor: 'pointer',
    },
  },
  subText: {
    color: brandColors.coolGray5,
  },
}))

export interface IDedicatedLanesContractRowProps {
  dedicatedLane: DedicatedLane
}

export const DedicatedLanesContractRow: FC<IDedicatedLanesContractRowProps> = ({
  dedicatedLane,
}) => {
  const { trackEvent } = useFMSAnalytics()
  const classes = useDedicatedLaneStyles()
  const history = useHistory()
  const {
    capacityPerWeek = '0',
    carrier,
    deliveryCity,
    deliveryRadiusMiles,
    deliveryState,
    equipment,
    expirationDate = '',
    id,
    isOnlyDropTrailer,
    pickupCity,
    pickupRadiusMiles,
    pickupState,
    rate,
  } = dedicatedLane || {}
  const { getFlag } = useFeatureFlagContext()
  const { enabled: isDedicatedLanesPageCapacityEnabled } = getFlag('dedicatedLanesPageCapacity')
  const { enabled: isDedicatedLanesExpiringSoonTagEnabled } = getFlag(
    'dedicatedLanesExpiringSoonTag'
  )
  const clickRowHandler = () => {
    trackEvent('Dedicated Lanes Page', 'VIEWED_DEDICATED_LANES_DETAILS_MODAL')
    history.push(`/dedicated-lanes/contracts/${id}`)
  }
  const { name: carrierName } = carrier || {}
  const expirationMessage = expirationDate ? 'Ends' : 'End date: Never'
  const loadingMethodMessage = isOnlyDropTrailer ? 'Drop' : 'Live'
  const rateMessage = rate ? formatCurrency(rate) : 'Rate Not Available'
  const rateTypeMessage = 'Rate Per Load'
  const showExpirationTag = shouldShowExpirationTag(dedicatedLane)

  return (
    <Box onClick={clickRowHandler}>
      <Card className={classes.root} noPadding>
        <Box p={2}>
          <Grid container>
            <Grid item md={11}>
              <Typography className={classes.subText} variant='body2'>
                {carrierName}
              </Typography>
              <Box display='flex' flexDirection='row' justifyContent='space-between'>
                {/* Lane details */}
                <Grid item md={5}>
                  <div>
                    <Box display='flex' alignItems='center'>
                      <Box pr={1}>
                        <Typography variant='body1'>
                          {`${pickupCity}, ${pickupState} `}
                          {pickupRadiusMiles !== undefined && pickupRadiusMiles > 0 && (
                            <Typography color='textSecondary' component='span' variant='body2'>
                              {`+${pickupRadiusMiles}`}
                            </Typography>
                          )}
                        </Typography>
                      </Box>
                      <LaneArrowIcon size='large' />
                      <Box pl={1}>
                        <Typography variant='body1'>
                          {`${deliveryCity}, ${deliveryState} `}
                          {deliveryRadiusMiles !== undefined && deliveryRadiusMiles > 0 && (
                            <Typography color='textSecondary' component='span' variant='body2'>
                              {`+${deliveryRadiusMiles}`}
                            </Typography>
                          )}
                        </Typography>
                      </Box>
                    </Box>
                    <Typography className={classes.subText} variant='body2'>
                      {`${equipment} · ${loadingMethodMessage} ${
                        expirationDate ? `· ${expirationMessage}` : ''
                      } `}
                      {expirationDate && (
                        <FormattedDateTime
                          inSentence
                          datetime={expirationDate}
                          displayTimezone={moment.tz.guess() as AllowedTimezones}
                          format='dateAndYear'
                        />
                      )}
                    </Typography>
                  </div>
                </Grid>
                {/* Rate */}
                <Grid item md={4}>
                  <div>
                    <Box display='flex' alignItems='center'>
                      <Typography variant='body1'>{rateMessage}</Typography>
                      <Tooltip
                        position='top'
                        type='dark'
                        trigger={
                          <Box pl={0.5} pt={0.5}>
                            <InfoIcon color='coolGray6' />
                          </Box>
                        }>
                        <div>Rate does not include fuel surcharges which may apply</div>
                      </Tooltip>
                    </Box>
                    <Typography className={classes.subText} variant='body2'>
                      {rateTypeMessage}
                    </Typography>
                  </div>
                </Grid>
                {/*  Loads amount */}
                {isDedicatedLanesPageCapacityEnabled && (
                  <Grid item md={3}>
                    <div>
                      <Box display='flex' alignItems='center'>
                        <Typography variant='body1'>Up to {capacityPerWeek} loads</Typography>
                        <Tooltip
                          position='top'
                          type='dark'
                          trigger={
                            <Box pl={0.5} pt={0.5}>
                              <InfoIcon color='coolGray6' />
                            </Box>
                          }>
                          <div>
                            {`The amount of loads you receive will be between 0-${capacityPerWeek} loads per week.`}
                          </div>
                        </Tooltip>
                      </Box>
                      <Typography className={classes.subText} variant='body2'>
                        Per week
                      </Typography>
                    </div>
                  </Grid>
                )}
              </Box>
              {showExpirationTag && isDedicatedLanesExpiringSoonTagEnabled && (
                <DedicatedLanesExpiringBadge />
              )}
            </Grid>
            <Grid alignItems='center' justifyContent='flex-end' container md={1}>
              <RightArrowIcon color='coolGray4' size='large' />
            </Grid>
          </Grid>
        </Box>
      </Card>
    </Box>
  )
}
